import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEstablishment } from '../components/EstablishmentSelector';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Package, 
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    XCircle,
    CheckCircle2,
    DollarSign,
    ShoppingCart,
    Trash2,
    RefreshCw,
    BarChart3,
    PieChart,
    Utensils,
    Coffee,
    Sparkles,
    Droplets,
    Box,
    AlertCircle,
    ArrowRight,
    Calculator,
    FileText,
    Plus,
    Minus,
    Search,
    ChevronRight,
    Clock,
    User,
    Calendar
} from 'lucide-react';
import { cn } from '../lib/utils';
import MobilePageHeader from '../components/MobilePageHeader';

// ============================================================
// DASHBOARD PROFESSIONNEL - GESTION DU STOCK
// Marges, Coûts, Pertes, Recettes, Alertes
// ============================================================

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(amount || 0)) + ' F';
};

// Product type icons and colors
const TYPE_CONFIG = {
    vendable: { label: 'Produit vendable', icon: ShoppingCart, color: 'text-green-600', bg: 'bg-green-100' },
    matiere_premiere: { label: 'Matière première', icon: Utensils, color: 'text-blue-600', bg: 'bg-blue-100' },
    consommable: { label: 'Consommable', icon: Sparkles, color: 'text-purple-600', bg: 'bg-purple-100' },
    entretien: { label: 'Entretien', icon: Droplets, color: 'text-orange-600', bg: 'bg-orange-100' },
};

// Margin status colors
const MARGIN_COLORS = {
    excellent: { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-50' },
    good: { bg: 'bg-green-400', text: 'text-green-600', light: 'bg-green-50' },
    average: { bg: 'bg-orange-400', text: 'text-orange-600', light: 'bg-orange-50' },
    low: { bg: 'bg-red-400', text: 'text-red-600', light: 'bg-red-50' },
    negative: { bg: 'bg-red-600', text: 'text-red-700', light: 'bg-red-100' },
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color, trend, onClick }) => (
    <Card 
        className={cn("cursor-pointer hover:shadow-md transition-shadow", onClick && "hover:border-primary")}
        onClick={onClick}
    >
        <CardContent className="p-4">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs text-muted-foreground">{title}</p>
                    <p className={cn("text-2xl font-bold mt-1", color)}>{value}</p>
                    {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                </div>
                <div className={cn("p-2 rounded-lg", color?.includes('red') ? 'bg-red-100' : color?.includes('green') ? 'bg-green-100' : color?.includes('orange') ? 'bg-orange-100' : 'bg-blue-100')}>
                    <Icon className={cn("w-5 h-5", color)} />
                </div>
            </div>
            {trend && (
                <div className={cn("flex items-center gap-1 mt-2 text-xs", trend > 0 ? 'text-green-600' : 'text-red-600')}>
                    {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {Math.abs(trend)}% vs mois dernier
                </div>
            )}
        </CardContent>
    </Card>
);

// Type Distribution Card
const TypeDistributionCard = ({ data }) => {
    const total = Object.values(data).reduce((sum, d) => sum + d.value, 0);
    
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Répartition par type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {Object.entries(data).map(([type, info]) => {
                    const config = TYPE_CONFIG[type] || TYPE_CONFIG.matiere_premiere;
                    const percentage = total > 0 ? (info.value / total * 100) : 0;
                    
                    return (
                        <div key={type} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <config.icon className={cn("w-3.5 h-3.5", config.color)} />
                                    <span>{config.label}</span>
                                    <Badge variant="outline" className="text-[10px] px-1">{info.count}</Badge>
                                </div>
                                <span className="font-medium">{formatCurrency(info.value)}</span>
                            </div>
                            <Progress value={percentage} className="h-1.5" />
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};

// Alert Card Component
const AlertCard = ({ title, items, type, icon: Icon, onViewAll }) => (
    <Card className={cn("border-l-4", type === 'critical' ? 'border-l-red-500' : type === 'warning' ? 'border-l-orange-500' : 'border-l-blue-500')}>
        <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Icon className={cn("w-4 h-4", type === 'critical' ? 'text-red-500' : type === 'warning' ? 'text-orange-500' : 'text-blue-500')} />
                    {title}
                </CardTitle>
                <Badge variant={type === 'critical' ? 'destructive' : 'secondary'}>{items.length}</Badge>
            </div>
        </CardHeader>
        <CardContent>
            {items.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-2">Aucune alerte</p>
            ) : (
                <div className="space-y-2">
                    {items.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg bg-muted/50">
                            <span className="font-medium truncate flex-1">{item.name}</span>
                            {item.stock !== undefined && (
                                <Badge variant="outline" className="ml-2">{item.stock} {item.unit}</Badge>
                            )}
                            {item.margin_rate !== undefined && (
                                <Badge variant={item.margin_rate < 0 ? 'destructive' : 'secondary'} className="ml-2">
                                    {item.margin_rate.toFixed(0)}%
                                </Badge>
                            )}
                        </div>
                    ))}
                    {items.length > 5 && (
                        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onViewAll}>
                            Voir tout ({items.length})
                            <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                    )}
                </div>
            )}
        </CardContent>
    </Card>
);

// Margin Analysis Row
const MarginRow = ({ product }) => {
    const colors = MARGIN_COLORS[product.margin_status?.status] || MARGIN_COLORS.average;
    
    return (
        <TableRow className={colors.light}>
            <TableCell className="font-medium text-sm py-2">{product.name}</TableCell>
            <TableCell className="text-xs py-2">{product.category}</TableCell>
            <TableCell className="text-right text-xs py-2">{formatCurrency(product.cost)}</TableCell>
            <TableCell className="text-right text-xs py-2">{formatCurrency(product.selling_price)}</TableCell>
            <TableCell className="text-right font-medium py-2">
                <span className={colors.text}>{formatCurrency(product.margin)}</span>
            </TableCell>
            <TableCell className="py-2">
                <Badge className={cn("text-[10px]", colors.bg, "text-white")}>
                    {product.margin_rate.toFixed(0)}%
                </Badge>
            </TableCell>
            <TableCell className="py-2">
                {product.has_recipe && (
                    <Badge variant="outline" className="text-[10px]">
                        <Utensils className="w-3 h-3 mr-1" />
                        Recette
                    </Badge>
                )}
            </TableCell>
        </TableRow>
    );
};

// Loss Record Dialog
const LossDialog = ({ open, onClose, products, onSubmit }) => {
    const [selectedProduct, setSelectedProduct] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [lossType, setLossType] = useState('perte');
    const [reason, setReason] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    const filteredProducts = products.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const handleSubmit = () => {
        if (!selectedProduct || !quantity) {
            toast.error('Veuillez sélectionner un produit et une quantité');
            return;
        }
        onSubmit({
            product_id: selectedProduct,
            quantity: parseFloat(quantity),
            loss_type: lossType,
            reason: reason
        });
        setSelectedProduct('');
        setQuantity('1');
        setReason('');
        onClose();
    };
    
    const selectedProductData = products.find(p => p.id === selectedProduct);
    
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-500" />
                        Enregistrer une perte
                    </DialogTitle>
                    <DialogDescription>
                        Enregistrez une perte, casse ou produit périmé
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-2">
                    {/* Product Search */}
                    <div>
                        <Label className="text-xs">Produit *</Label>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher un produit..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8 h-9"
                            />
                        </div>
                        {searchTerm && filteredProducts.length > 0 && (
                            <div className="mt-1 max-h-32 overflow-y-auto border rounded-lg">
                                {filteredProducts.slice(0, 5).map(p => (
                                    <button
                                        key={p.id}
                                        className={cn(
                                            "w-full text-left px-3 py-2 text-sm hover:bg-muted",
                                            selectedProduct === p.id && "bg-primary/10"
                                        )}
                                        onClick={() => {
                                            setSelectedProduct(p.id);
                                            setSearchTerm(p.name);
                                        }}
                                    >
                                        {p.name}
                                        <span className="text-xs text-muted-foreground ml-2">
                                            (Stock: {p.stock_quantity_base} {p.unit_base})
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Quantity and Type */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Quantité *</Label>
                            <Input
                                type="number"
                                min="0.1"
                                step="0.1"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className="h-9"
                            />
                            {selectedProductData && (
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    Stock actuel: {selectedProductData.stock_quantity_base} {selectedProductData.unit_base}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs">Type de perte *</Label>
                            <Select value={lossType} onValueChange={setLossType}>
                                <SelectTrigger className="h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="perte">Perte générale</SelectItem>
                                    <SelectItem value="perime">Produit périmé</SelectItem>
                                    <SelectItem value="casse">Casse / Bris</SelectItem>
                                    <SelectItem value="erreur">Erreur manipulation</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    {/* Reason */}
                    <div>
                        <Label className="text-xs">Raison (optionnel)</Label>
                        <Input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Détails de la perte..."
                            className="h-9"
                        />
                    </div>
                </div>
                
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} size="sm">Annuler</Button>
                    <Button onClick={handleSubmit} size="sm" className="bg-red-600 hover:bg-red-700">
                        Enregistrer la perte
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Main Dashboard Component
const StockDashboardPage = () => {
    const { api } = useAuth();
    const { selectedEstablishmentId } = useEstablishment();
    
    // Data state
    const [kpis, setKpis] = useState(null);
    const [profitability, setProfitability] = useState(null);
    const [reorderSuggestions, setReorderSuggestions] = useState(null);
    const [losses, setLosses] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    // Weekly report & alerts state
    const [weeklyReport, setWeeklyReport] = useState(null);
    const [lossAlerts, setLossAlerts] = useState(null);
    const [alertSettings, setAlertSettings] = useState({
        weekly_threshold_cfa: 10000,
        enable_email_alert: false,
        alert_email: '',
        enable_dashboard_alert: true
    });
    const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
    const [loadingWeekly, setLoadingWeekly] = useState(false);
    
    // UI state
    const [activeTab, setActiveTab] = useState('overview');
    const [lossDialogOpen, setLossDialogOpen] = useState(false);
    
    // Fetch weekly report
    const fetchWeeklyReport = useCallback(async () => {
        try {
            setLoadingWeekly(true);
            const [reportRes, alertsRes, settingsRes] = await Promise.all([
                api().get('/stock-dashboard/losses/weekly-report'),
                api().get('/stock-dashboard/losses/alerts'),
                api().get('/stock-dashboard/losses/settings')
            ]);
            setWeeklyReport(reportRes.data);
            setLossAlerts(alertsRes.data);
            setAlertSettings(settingsRes.data);
        } catch (error) {
            console.error('Weekly report fetch error:', error);
        } finally {
            setLoadingWeekly(false);
        }
    }, [api]);
    
    // Save alert settings
    const saveAlertSettings = async () => {
        try {
            await api().put('/stock-dashboard/losses/settings', alertSettings);
            toast.success('Paramètres d\'alertes enregistrés');
            setAlertSettingsOpen(false);
            fetchWeeklyReport(); // Refresh alerts
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        }
    };
    
    // Fetch all dashboard data
    const fetchData = useCallback(async () => {
        try {
            setRefreshing(true);
            const params = selectedEstablishmentId ? `?establishment_id=${selectedEstablishmentId}` : '';
            
            const [kpisRes, profitRes, reorderRes, lossesRes, recipesRes, productsRes] = await Promise.all([
                api().get(`/stock-dashboard/kpis${params}`),
                api().get(`/stock-dashboard/profitability${params}`),
                api().get(`/stock-dashboard/reorder-suggestions${params}`),
                api().get('/stock-dashboard/losses?days=30'),
                api().get('/stock-dashboard/recipes'),
                api().get('/products'),
            ]);
            
            setKpis(kpisRes.data);
            setProfitability(profitRes.data);
            setReorderSuggestions(reorderRes.data);
            setLosses(lossesRes.data);
            setRecipes(recipesRes.data || []);
            setProducts(productsRes.data || []);
            
            // Also fetch alerts
            fetchWeeklyReport();
        } catch (error) {
            console.error('Dashboard fetch error:', error);
            toast.error('Erreur lors du chargement du dashboard');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [api, selectedEstablishmentId, fetchWeeklyReport]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Handle loss submission
    const handleLossSubmit = async (lossData) => {
        try {
            await api().post('/stock-dashboard/losses', lossData);
            toast.success('Perte enregistrée');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }
    
    return (
        <div className="space-y-4 p-4 md:p-6" data-testid="stock-dashboard-page">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Dashboard Stock"
                subtitle="Marges · Pertes · Alertes"
                icon={BarChart3}
                backPath="/dashboard"
            />
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-xl font-bold">Dashboard Stock</h1>
                    <p className="text-xs text-muted-foreground">Vision globale · Marges · Pertes · Alertes</p>
                </div>
                
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLossDialogOpen(true)}
                        className="h-8"
                    >
                        <Trash2 className="w-4 h-4 mr-1 text-red-500" />
                        Perte
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={fetchData}
                        disabled={refreshing}
                        className="h-8"
                    >
                        <RefreshCw className={cn("w-4 h-4", refreshing && 'animate-spin')} />
                    </Button>
                </div>
            </div>
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="h-9">
                    <TabsTrigger value="overview" className="text-xs">
                        <BarChart3 className="w-3.5 h-3.5 mr-1" />
                        Vue d'ensemble
                    </TabsTrigger>
                    <TabsTrigger value="margins" className="text-xs">
                        <Calculator className="w-3.5 h-3.5 mr-1" />
                        Marges
                    </TabsTrigger>
                    <TabsTrigger value="reorder" className="text-xs">
                        <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                        Réappro
                    </TabsTrigger>
                    <TabsTrigger value="losses" className="text-xs">
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Pertes
                    </TabsTrigger>
                    <TabsTrigger value="recipes" className="text-xs">
                        <Utensils className="w-3.5 h-3.5 mr-1" />
                        Recettes
                    </TabsTrigger>
                </TabsList>
                
                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                    {/* Main KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        <KPICard 
                            title="Valeur totale du stock" 
                            value={formatCurrency(kpis?.total_stock_value)}
                            icon={DollarSign}
                            color="text-blue-600"
                        />
                        <KPICard 
                            title="Matières premières" 
                            value={formatCurrency(kpis?.total_matiere_premiere_value)}
                            icon={Utensils}
                            color="text-green-600"
                        />
                        <KPICard 
                            title="Produits" 
                            value={kpis?.total_products || 0}
                            subtitle={`${kpis?.products_by_type?.vendable?.count || 0} vendables`}
                            icon={Package}
                            color="text-purple-600"
                        />
                        <KPICard 
                            title="En rupture" 
                            value={kpis?.alerts?.out_of_stock_count || 0}
                            icon={XCircle}
                            color={kpis?.alerts?.out_of_stock_count > 0 ? "text-red-600" : "text-gray-400"}
                        />
                        <KPICard 
                            title="Stock faible" 
                            value={kpis?.alerts?.low_stock_count || 0}
                            icon={AlertTriangle}
                            color={kpis?.alerts?.low_stock_count > 0 ? "text-orange-600" : "text-gray-400"}
                        />
                        <KPICard 
                            title="Pertes (30j)" 
                            value={formatCurrency(kpis?.losses_30_days?.value)}
                            subtitle={`${kpis?.losses_30_days?.count || 0} incidents`}
                            icon={Trash2}
                            color="text-red-600"
                        />
                    </div>
                    
                    {/* Type Distribution + Alerts */}
                    <div className="grid md:grid-cols-3 gap-4">
                        <TypeDistributionCard data={kpis?.products_by_type || {}} />
                        
                        <AlertCard 
                            title="Produits en rupture" 
                            items={kpis?.alerts?.out_of_stock || []}
                            type="critical"
                            icon={XCircle}
                        />
                        
                        <AlertCard 
                            title="Stock faible" 
                            items={kpis?.alerts?.low_stock || []}
                            type="warning"
                            icon={AlertTriangle}
                        />
                    </div>
                    
                    {/* Margin Alerts */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <AlertCard 
                            title="Marges faibles" 
                            items={kpis?.alerts?.low_margin || []}
                            type="warning"
                            icon={TrendingDown}
                        />
                        
                        <AlertCard 
                            title="Marges négatives ⚠️" 
                            items={kpis?.alerts?.negative_margin || []}
                            type="critical"
                            icon={AlertCircle}
                        />
                    </div>
                </TabsContent>
                
                {/* Margins Tab */}
                <TabsContent value="margins" className="space-y-4 mt-4">
                    {/* Margin Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Card className="bg-green-50 border-green-200">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-green-700">Excellente (≥40%)</p>
                                <p className="text-2xl font-bold text-green-700">{profitability?.summary?.excellent_margin || 0}</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-green-50/50 border-green-100">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-green-600">Bonne (25-40%)</p>
                                <p className="text-2xl font-bold text-green-600">{profitability?.summary?.good_margin || 0}</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-orange-50 border-orange-200">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-orange-600">Moyenne (15-25%)</p>
                                <p className="text-2xl font-bold text-orange-600">{profitability?.summary?.average_margin || 0}</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-red-50 border-red-200">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-red-600">Faible (0-15%)</p>
                                <p className="text-2xl font-bold text-red-600">{profitability?.summary?.low_margin || 0}</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-red-100 border-red-300">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-red-700">Négative (&lt;0%)</p>
                                <p className="text-2xl font-bold text-red-700">{profitability?.summary?.negative_margin || 0}</p>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* Margin Table */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Analyse des marges par produit</CardTitle>
                            <CardDescription className="text-xs">
                                Triés par marge croissante (les moins rentables en premier)
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="text-xs">Produit</TableHead>
                                            <TableHead className="text-xs">Catégorie</TableHead>
                                            <TableHead className="text-xs text-right">Coût</TableHead>
                                            <TableHead className="text-xs text-right">Prix vente</TableHead>
                                            <TableHead className="text-xs text-right">Marge</TableHead>
                                            <TableHead className="text-xs">Taux</TableHead>
                                            <TableHead className="text-xs">Recette</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(profitability?.products || []).slice(0, 30).map(product => (
                                            <MarginRow key={product.id} product={product} />
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Reorder Tab */}
                <TabsContent value="reorder" className="space-y-4 mt-4">
                    {/* Reorder Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KPICard 
                            title="Produits à commander" 
                            value={reorderSuggestions?.summary?.total_products || 0}
                            icon={ShoppingCart}
                            color="text-blue-600"
                        />
                        <KPICard 
                            title="Urgents (rupture)" 
                            value={reorderSuggestions?.summary?.critical || 0}
                            icon={XCircle}
                            color="text-red-600"
                        />
                        <KPICard 
                            title="Prioritaires" 
                            value={reorderSuggestions?.summary?.high || 0}
                            icon={AlertTriangle}
                            color="text-orange-600"
                        />
                        <KPICard 
                            title="Coût estimé" 
                            value={formatCurrency(reorderSuggestions?.summary?.total_estimated_cost)}
                            icon={DollarSign}
                            color="text-purple-600"
                        />
                    </div>
                    
                    {/* Reorder List */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Liste de réapprovisionnement suggérée</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="text-xs">Urgence</TableHead>
                                            <TableHead className="text-xs">Produit</TableHead>
                                            <TableHead className="text-xs">Type</TableHead>
                                            <TableHead className="text-xs text-right">Stock actuel</TableHead>
                                            <TableHead className="text-xs text-right">Seuil</TableHead>
                                            <TableHead className="text-xs text-right">Qté suggérée</TableHead>
                                            <TableHead className="text-xs text-right">Coût estimé</TableHead>
                                            <TableHead className="text-xs">Fournisseur</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(reorderSuggestions?.suggestions || []).map(item => (
                                            <TableRow 
                                                key={item.id}
                                                className={cn(
                                                    item.urgency === 'critical' ? 'bg-red-50' :
                                                    item.urgency === 'high' ? 'bg-orange-50' : ''
                                                )}
                                            >
                                                <TableCell className="py-2">
                                                    <Badge 
                                                        variant={item.urgency === 'critical' ? 'destructive' : 'secondary'}
                                                        className="text-[10px]"
                                                    >
                                                        {item.urgency === 'critical' ? 'URGENT' : item.urgency === 'high' ? 'Prioritaire' : 'Normal'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="font-medium text-sm py-2">{item.name}</TableCell>
                                                <TableCell className="text-xs py-2">
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {TYPE_CONFIG[item.type]?.label || item.type}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right text-xs py-2">
                                                    <span className={item.current_stock <= 0 ? 'text-red-600 font-bold' : ''}>
                                                        {item.current_stock} {item.unit}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-right text-xs py-2">{item.threshold}</TableCell>
                                                <TableCell className="text-right font-medium text-sm py-2">
                                                    {item.suggested_packs} {item.pack_unit}
                                                </TableCell>
                                                <TableCell className="text-right text-xs py-2">{formatCurrency(item.estimated_cost)}</TableCell>
                                                <TableCell className="text-xs py-2">{item.supplier || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Losses Tab */}
                <TabsContent value="losses" className="space-y-4 mt-4">
                    {/* Weekly Alert Banner (if triggered) */}
                    {lossAlerts?.has_critical && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-red-800">Alerte Pertes Activée</h3>
                                    {lossAlerts.alerts?.map(alert => (
                                        <p key={alert.id} className="text-sm text-red-700 mt-1">
                                            {alert.message}
                                        </p>
                                    ))}
                                </div>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setAlertSettingsOpen(true)}
                                    className="border-red-300 text-red-700 hover:bg-red-100"
                                >
                                    Configurer
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {/* Loss Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KPICard 
                            title="Pertes (30 jours)" 
                            value={losses?.total_count || 0}
                            subtitle="incidents"
                            icon={Trash2}
                            color="text-red-600"
                        />
                        <KPICard 
                            title="Valeur totale perdue" 
                            value={formatCurrency(losses?.total_value)}
                            icon={DollarSign}
                            color="text-red-600"
                        />
                        {Object.entries(losses?.by_type || {}).slice(0, 2).map(([type, data]) => (
                            <KPICard 
                                key={type}
                                title={type === 'perte' ? 'Pertes générales' : type === 'perime' ? 'Périmés' : type === 'casse' ? 'Casse' : type}
                                value={data.count}
                                subtitle={formatCurrency(data.value)}
                                icon={type === 'perime' ? Clock : type === 'casse' ? AlertCircle : Trash2}
                                color="text-orange-600"
                            />
                        ))}
                    </div>
                    
                    {/* Weekly Report Card */}
                    <Card className="border-l-4 border-l-blue-500">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-blue-500" />
                                        Rapport Hebdomadaire
                                    </CardTitle>
                                    <CardDescription className="text-xs">7 derniers jours</CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => setAlertSettingsOpen(true)}
                                        className="h-8"
                                    >
                                        <AlertTriangle className="w-4 h-4 mr-1" />
                                        Paramètres alertes
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={fetchWeeklyReport}
                                        className="h-8"
                                    >
                                        <RefreshCw className={cn("w-4 h-4", loadingWeekly && 'animate-spin')} />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {weeklyReport ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Summary */}
                                    <div className="space-y-2">
                                        <p className="text-xs text-muted-foreground">Total cette semaine</p>
                                        <p className="text-2xl font-bold text-red-600">
                                            {formatCurrency(weeklyReport.summary?.total_value_cfa)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {weeklyReport.summary?.total_count || 0} incidents
                                        </p>
                                        
                                        {/* Comparison */}
                                        {weeklyReport.comparison && (
                                            <div className={cn(
                                                "flex items-center gap-1 text-xs mt-2 p-2 rounded-lg",
                                                weeklyReport.comparison.trend === 'up' ? 'bg-red-50 text-red-700' : 
                                                weeklyReport.comparison.trend === 'down' ? 'bg-green-50 text-green-700' : 
                                                'bg-gray-50 text-gray-700'
                                            )}>
                                                {weeklyReport.comparison.trend === 'up' ? (
                                                    <TrendingUp className="w-4 h-4" />
                                                ) : weeklyReport.comparison.trend === 'down' ? (
                                                    <TrendingDown className="w-4 h-4" />
                                                ) : null}
                                                <span>
                                                    {weeklyReport.comparison.variation_percent > 0 ? '+' : ''}
                                                    {weeklyReport.comparison.variation_percent}% vs semaine précédente
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* By Type */}
                                    <div className="space-y-2">
                                        <p className="text-xs text-muted-foreground">Par type de perte</p>
                                        {Object.entries(weeklyReport.breakdown?.by_type || {}).map(([type, data]) => (
                                            <div key={type} className="flex justify-between items-center text-sm">
                                                <span className="capitalize">{type}</span>
                                                <span className="font-medium">{data.count}x - {formatCurrency(data.value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Top Products */}
                                    <div className="space-y-2">
                                        <p className="text-xs text-muted-foreground">Produits les plus perdus</p>
                                        {(weeklyReport.top_products_lost || []).slice(0, 3).map((product, idx) => (
                                            <div key={idx} className="flex justify-between items-center text-sm">
                                                <span className="truncate">{product.product_name}</span>
                                                <Badge variant="outline" className="text-[10px]">
                                                    {formatCurrency(product.total_value)}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <Button variant="outline" onClick={fetchWeeklyReport}>
                                        Charger le rapport
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    
                    {/* Loss History */}
                    <Card>
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="text-sm">Historique des pertes</CardTitle>
                                <CardDescription className="text-xs">30 derniers jours</CardDescription>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setLossDialogOpen(true)}>
                                <Plus className="w-4 h-4 mr-1" />
                                Nouvelle perte
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="text-xs">Date</TableHead>
                                            <TableHead className="text-xs">Produit</TableHead>
                                            <TableHead className="text-xs">Type</TableHead>
                                            <TableHead className="text-xs text-right">Quantité</TableHead>
                                            <TableHead className="text-xs text-right">Valeur</TableHead>
                                            <TableHead className="text-xs">Par</TableHead>
                                            <TableHead className="text-xs">Raison</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(losses?.losses || []).slice(0, 20).map(loss => (
                                            <TableRow key={loss.id}>
                                                <TableCell className="text-xs py-2">
                                                    {new Date(loss.created_at).toLocaleDateString('fr-FR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </TableCell>
                                                <TableCell className="font-medium text-sm py-2">{loss.product_name}</TableCell>
                                                <TableCell className="py-2">
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {loss.loss_type === 'perte' ? 'Perte' : 
                                                         loss.loss_type === 'perime' ? 'Périmé' :
                                                         loss.loss_type === 'casse' ? 'Casse' : loss.loss_type}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right text-xs py-2">{loss.quantity} {loss.unit}</TableCell>
                                                <TableCell className="text-right text-xs font-medium text-red-600 py-2">
                                                    {formatCurrency(loss.value)}
                                                </TableCell>
                                                <TableCell className="text-xs py-2">{loss.user_name}</TableCell>
                                                <TableCell className="text-xs py-2 text-muted-foreground">{loss.reason || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Alert Settings Dialog */}
                    <Dialog open={alertSettingsOpen} onOpenChange={setAlertSettingsOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                                    Paramètres d'alertes pertes
                                </DialogTitle>
                                <DialogDescription>
                                    Configurez les seuils et notifications pour les pertes
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div>
                                    <Label className="text-sm">Seuil hebdomadaire (F CFA)</Label>
                                    <Input
                                        type="number"
                                        value={alertSettings.weekly_threshold_cfa}
                                        onChange={(e) => setAlertSettings(prev => ({
                                            ...prev,
                                            weekly_threshold_cfa: parseFloat(e.target.value) || 0
                                        }))}
                                        className="mt-1"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Une alerte sera déclenchée si les pertes hebdomadaires dépassent ce montant
                                    </p>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-sm">Alerte Dashboard</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Afficher une bannière d'alerte sur le dashboard
                                        </p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={alertSettings.enable_dashboard_alert}
                                        onChange={(e) => setAlertSettings(prev => ({
                                            ...prev,
                                            enable_dashboard_alert: e.target.checked
                                        }))}
                                        className="w-5 h-5 accent-primary"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-sm">Alerte Email</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Envoyer un email hebdomadaire
                                        </p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={alertSettings.enable_email_alert}
                                        onChange={(e) => setAlertSettings(prev => ({
                                            ...prev,
                                            enable_email_alert: e.target.checked
                                        }))}
                                        className="w-5 h-5 accent-primary"
                                    />
                                </div>
                                
                                {alertSettings.enable_email_alert && (
                                    <div>
                                        <Label className="text-sm">Email de notification</Label>
                                        <Input
                                            type="email"
                                            value={alertSettings.alert_email || ''}
                                            onChange={(e) => setAlertSettings(prev => ({
                                                ...prev,
                                                alert_email: e.target.value
                                            }))}
                                            placeholder="admin@restaurant.com"
                                            className="mt-1"
                                        />
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setAlertSettingsOpen(false)}>
                                    Annuler
                                </Button>
                                <Button onClick={saveAlertSettings}>
                                    Enregistrer
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>
                
                {/* Recipes Tab */}
                <TabsContent value="recipes" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recettes et coûts de revient</CardTitle>
                            <CardDescription className="text-xs">
                                Gérez les recettes pour calculer automatiquement vos coûts et marges
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {recipes.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>Aucune recette créée</p>
                                    <p className="text-xs mt-1">
                                        Créez des recettes pour calculer automatiquement vos coûts de revient
                                    </p>
                                    <Button className="mt-4" variant="outline" size="sm">
                                        <Plus className="w-4 h-4 mr-1" />
                                        Créer une recette
                                    </Button>
                                </div>
                            ) : (
                                <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-muted/50">
                                                <TableHead className="text-xs">Recette</TableHead>
                                                <TableHead className="text-xs">Ingrédients</TableHead>
                                                <TableHead className="text-xs text-right">Coût</TableHead>
                                                <TableHead className="text-xs text-right">Prix vente</TableHead>
                                                <TableHead className="text-xs text-right">Marge</TableHead>
                                                <TableHead className="text-xs">Taux</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {recipes.map(recipe => {
                                                const colors = MARGIN_COLORS[recipe.margin_status?.status] || MARGIN_COLORS.average;
                                                return (
                                                    <TableRow key={recipe.id} className={colors.light}>
                                                        <TableCell className="font-medium text-sm py-2">{recipe.name}</TableCell>
                                                        <TableCell className="py-2">
                                                            <Badge variant="outline" className="text-[10px]">
                                                                {recipe.ingredients?.length || 0} ingrédients
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right text-xs py-2">{formatCurrency(recipe.total_cost)}</TableCell>
                                                        <TableCell className="text-right text-xs py-2">{formatCurrency(recipe.selling_price_cfa)}</TableCell>
                                                        <TableCell className="text-right font-medium py-2">
                                                            <span className={colors.text}>{formatCurrency(recipe.margin)}</span>
                                                        </TableCell>
                                                        <TableCell className="py-2">
                                                            <Badge className={cn("text-[10px]", colors.bg, "text-white")}>
                                                                {recipe.margin_rate?.toFixed(0) || 0}%
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            
            {/* Loss Dialog */}
            <LossDialog 
                open={lossDialogOpen}
                onClose={() => setLossDialogOpen(false)}
                products={products}
                onSubmit={handleLossSubmit}
            />
        </div>
    );
};

export default StockDashboardPage;
